/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { BuilderComponentProps } from '../../../builder';
import { MEDIA_MIN_WIDTH } from '../../../constants';
import { RichTextHtml } from '../../../components/RichTextHtml/RichTextHtml';
import { InViewTracking } from '../../../components/InViewTracking/InViewTracking';
import { useDateTime } from '../utils';

type Speaker = { name: 'string'; title: 'string'; imageURL: 'string' };

type BackgroundImages = { tall?: string; wide?: string; square?: string };
type PlaceholderImages = {
  mobile?: string;
  tablet?: string;
  desktop?: string;
};

interface Props extends BuilderComponentProps {
  isFeatured?: boolean;
  type: 'Keynote' | 'Networking' | 'Breakout';
  title: 'string';
  description: 'string';
  track: 'All tracks' | 'Build' | 'Attract' | 'Innovate' | 'Extend';
  startTime: 'string';
  endTime: 'string';
  speakers: Speaker[];
  placeholderImages?: PlaceholderImages;
  backgroundImages?: BackgroundImages;
}

const typeColors = {
  'All tracks': '#DDFF76',
  Build: Colors.lavender,
  Attract: Colors.peach,
  Innovate: Colors.sky,
  Extend: Colors.bolt,
};

const border = `1px solid ${Colors.earth}`;

const Styles = labelStyles('Session', {
  root: (isFeatured: boolean) =>
    css({
      borderTop: border,
      marginTop: '-1px',
      [MEDIA_MIN_WIDTH.small]: { flexDirection: 'row' },
      ...(isFeatured && {
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column-reverse',
        [MEDIA_MIN_WIDTH.small]: { flexDirection: 'row' },
      }),
    }),
  contentWrapper: (isFeatured: boolean) =>
    css({
      display: 'flex',
      padding: '29px 24px 47.5px 24px',
      color: Colors.earth,
      [MEDIA_MIN_WIDTH.small]: {
        padding: '29.5px 30px 38.5px 30px',
      },
      [MEDIA_MIN_WIDTH.large]: {
        padding: '29.5px 50px 38.5px 30px',
      },
      ...(isFeatured && {
        width: 'auto',
        [MEDIA_MIN_WIDTH.small]: { width: '60%' },
        [MEDIA_MIN_WIDTH.extraLarge]: { width: '50%' },
      }),
    }),
  content: (isFeatured: boolean) =>
    css({
      [MEDIA_MIN_WIDTH.small]: {
        width: isFeatured ? '100%' : '70%',
      },
      [MEDIA_MIN_WIDTH.large]: {
        width: '100%',
      },
    }),
  data: (isFeatured: boolean) =>
    css({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      [MEDIA_MIN_WIDTH.small]: {
        alignItems: 'center',
        flexDirection: 'row',
      },
      [MEDIA_MIN_WIDTH.large]: {
        gap: isFeatured ? '20px' : '42.25px',
      },
    }),
  trackTypeContainer: (isFeatured: boolean) =>
    css({
      [MEDIA_MIN_WIDTH.small]: {
        whiteSpace: 'nowrap',
      },
      [MEDIA_MIN_WIDTH.large]: {
        width: isFeatured ? 'auto' : '50%',
      },
    }),
  track: (track: Props['track']) =>
    css({
      display: 'inline-block',
      fontFamily: Typography.paragraph1.fontFamily,
      fontSize: '15px',
      fontWeight: 500,
      borderRadius: '30px',
      lineHeight: '138%',
      letterSpacing: '0.02em',
      padding: '3px 8px',
      backgroundColor: typeColors[track],
    }),
  type: css({
    borderBottom: `1.5px solid ${Colors.earth}`,
    display: 'inline-block',
    fontFamily: 'Chivo Mono',
    fontSize: '15px',
    fontWeight: 500,
    lineHeight: '16px',
    marginLeft: '20px',
  }),
  time: css({
    fontFamily: 'Chivo Mono',
    fontSize: '15px',
    fontWeight: 500,
    borderRadius: '30px',
    lineHeight: '130%',
    marginTop: '20px',
    [MEDIA_MIN_WIDTH.small]: {
      marginTop: 0,
      marginLeft: '20px',
    },
    [MEDIA_MIN_WIDTH.large]: {
      marginLeft: 0,
      width: '50%',
    },
  }),
  titleDescriptionContainer: (isFeatured: boolean) =>
    css({
      [MEDIA_MIN_WIDTH.large]: {
        width: isFeatured ? '100%' : '50%',
      },
    }),
  title: (isFeatured: boolean) =>
    css({
      fontFamily: Typography.heading.fontFamily,
      fontSize: isFeatured ? '40px' : '26px',
      fontWeight: 500,
      lineHeight: isFeatured ? '44px' : '110%',
      letterSpacing: isFeatured ? '1.5px' : '0.012em',
      marginBottom: '30px',
      [MEDIA_MIN_WIDTH.small]: {
        lineHeight: isFeatured ? '44px' : '130%',
        marginBottom: '27px',
      },
      [MEDIA_MIN_WIDTH.large]: {
        fontSize: isFeatured ? '40px' : '25px',
        lineHeight: isFeatured ? '44px' : '30px',
        letterSpacing: '0.022em',
      },
    }),
  description: (isFeatured: boolean) =>
    css({
      fontSize: isFeatured ? '18px' : '15px',
      ...(isFeatured && { lineHeight: '24px' }),
    }),
  speakers: (isFeatured: boolean) =>
    css({
      display: 'grid',
      gap: '27px',
      gridTemplateColumns: '1fr',
      [MEDIA_MIN_WIDTH.small]: {
        ...(!isFeatured && {
          columnGap: '30.5px',
          gridTemplateColumns: '1fr 1fr',
        }),
      },
      [MEDIA_MIN_WIDTH.large]: {
        gap: '40px',
        width: isFeatured ? '100%' : '50%',
      },
    }),
  speakerName: css({
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
  }),
  speakerTitle: css({
    fontSize: '14px',
    lineHeight: '20px',
  }),
  sessionDetails: (isFeatured: boolean) =>
    css({
      display: 'flex',
      flexDirection: 'column',
      gap: '30px',
      marginTop: isFeatured ? '40px' : '30px',
      [MEDIA_MIN_WIDTH.small]: {
        gap: '40px',
        marginTop: '27px',
      },
      [MEDIA_MIN_WIDTH.large]: {
        flexDirection: isFeatured ? 'column' : 'row',
        marginTop: '40px',
      },
    }),
  media: css({
    minWidth: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    borderBottom: border,

    [MEDIA_MIN_WIDTH.small]: {
      borderBottom: 'none',
      borderLeft: border,
      minWidth: '35%',
      width: '35%',
      flexDirection: 'column',
    },
    [MEDIA_MIN_WIDTH.extraLarge]: {
      minWidth: '50%',
      width: '50%',
      flexDirection: 'row',
      alignItems: 'start',
    },
  }),
  placeholderContainer: (backgroundImages: Props['placeholderImages']) =>
    css({
      backgroundSize: 'cover',
      display: 'flex',
      width: '100%',
      height: '250px',
      justifyContent: 'end',
      ...(backgroundImages?.mobile && {
        backgroundImage: `url("${backgroundImages.mobile}")`,
      }),
      [MEDIA_MIN_WIDTH.small]: {
        height: '100%',
        ...(backgroundImages?.tablet && {
          backgroundImage: `url("${backgroundImages.tablet}")`,
        }),
      },
      [MEDIA_MIN_WIDTH.large]: {
        ...(backgroundImages?.desktop && {
          backgroundImage: `url("${backgroundImages.desktop}")`,
        }),
      },
    }),
  speakerImagesContainer: (
    backgroundImages: BackgroundImages,
    noOfImages: number,
  ) =>
    css({
      width: '100%',
      height: '100%',
      ...(backgroundImages.tall && {
        backgroundImage: `url("${backgroundImages.tall}")`,
      }),
      backgroundSize: 'contain',
      backgroundPosition: 'bottom',
      [MEDIA_MIN_WIDTH.small]: {
        ...(backgroundImages?.wide && {
          backgroundImage: `url("${backgroundImages.wide}")`,
        }),
        ...(noOfImages === 1 && { backgroundSize: '100% 50%' }),
        ...(noOfImages > 1 && {
          backgroundSize: '100% 15%',
        }),
      },
      [MEDIA_MIN_WIDTH.large]: {
        backgroundPositionX: 'right',
        ...(backgroundImages && {
          backgroundImage:
            noOfImages > 1
              ? `url("${backgroundImages.wide}")`
              : `url("${backgroundImages.square}")`,
        }),

        ...(noOfImages > 1 && {
          backgroundSize: '100% 15%',
        }),
      },
      [MEDIA_MIN_WIDTH.extraLarge]: {
        ...(noOfImages === 1 &&
          backgroundImages?.tall && {
            backgroundImage: `url("${backgroundImages.tall}")`,
            backgroundSize: '10% 100%',
          }),
        ...(noOfImages === 2 &&
          backgroundImages?.wide && {
            backgroundImage: `url("${backgroundImages?.wide}")`,
            backgroundSize: '100% 50%',
          }),
        ...(noOfImages > 2 &&
          backgroundImages?.square && {
            backgroundImage: `url("${backgroundImages?.square}")`,
            backgroundSize: '50% 50%',
          }),
      },
    }),
  speakerImages: (noOfImages: number) => {
    const isSingleImage = noOfImages === 1;
    const isEvenNoOfImages = noOfImages % 2 === 0;
    return css({
      display: 'grid',
      width: '90%',
      gridTemplateColumns: `repeat(${noOfImages}, 1fr)`,
      overflow: 'hidden',
      borderRight: border,
      '& > div:not(:first-child)': {
        borderLeft: border,
      },

      [MEDIA_MIN_WIDTH.small]: {
        width: '100%',
        maxHeight: '85%',
        minHeight: '50%',
        height: noOfImages > 2 ? '90%' : 'auto',
        display: 'grid',
        gridTemplateColumns: '100%',
        gridTemplateRows: `repeat(${noOfImages}, 1fr)`,
        borderBottom: border,
        '& > div:not(:first-child)': {
          borderLeft: 'none',
          borderTop: border,
        },
      },
      [MEDIA_MIN_WIDTH.extraLarge]: {
        width: isSingleImage ? '90%' : '100%',
        height: isSingleImage ? '100%' : 'fit-content',
        display: isSingleImage ? 'flex' : 'grid',
        gridTemplateColumns: '50% 50%',
        ...(noOfImages !== 2 && { gridTemplateRows: '50% 50%' }),
        minHeight: '100%',
        '& > div:not(:first-child)': {
          borderTop: 'none',
        },
        '& > div:nth-of-type(even)': {
          borderLeft: border,
        },
        ...(noOfImages > 2 &&
          !isEvenNoOfImages && {
            rowGap: '1px',
            '& > div:nth-child(-n+2)': {
              borderBottom: border,
            },
            '& > div:last-child': {
              borderRight: border,
            },
          }),
        ...(noOfImages === 2 && {
          '& > div:nth-child(-n+2)': {
            borderBottom: border,
          },
        }),
        borderBottom: isEvenNoOfImages && noOfImages !== 2 ? border : 'none',
      },
    });
  },
  speakerImageContainer: (isSingleImage: boolean) =>
    css({
      display: 'flex',
      justifyContent: 'center',
      minHeight: '100%',
      maxWidth: '100%',
      backgroundColor: Colors.white,
      [MEDIA_MIN_WIDTH.small]: {
        overflow: 'hidden',
      },
      [MEDIA_MIN_WIDTH.extraLarge]: {
        height: 'auto',
        display: 'flex',
      },
      ...(isSingleImage && { aspectRatio: '250/167' }),
    }),
  speakerImage: css({
    display: 'block',
    margin: '0 auto',
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'cover',
  }),
});

export function Session(props: Props) {
  const { shortTime: startTime } = useDateTime(props.startTime);
  const { shortTime: endTime } = useDateTime(props.endTime);

  const speakerImages = (props.speakers || []).filter(
    (speaker) => speaker.imageURL,
  );

  return (
    <InViewTracking
      sectionName="Session"
      layerName={props.builderBlock?.layerName || props.title}
      componentName={props.builderBlock?.component?.name}
    >
      <div css={Styles.root(props.isFeatured)}>
        <div css={Styles.contentWrapper(props.isFeatured)}>
          <div css={Styles.content(props.isFeatured)}>
            <div css={Styles.data(props.isFeatured)}>
              <div css={Styles.trackTypeContainer(props.isFeatured)}>
                <span css={Styles.track(props.track)}>
                  <p>{props.track}</p>
                </span>
                <p css={Styles.type}>{props.type}</p>
              </div>
              <div css={Styles.time}>
                <p>
                  {startTime}
                  {props.endTime && ` — ${endTime}`}
                </p>
              </div>
            </div>
            <div css={Styles.sessionDetails(props.isFeatured)}>
              <div css={Styles.titleDescriptionContainer(props.isFeatured)}>
                <h3 css={Styles.title(props.isFeatured)}>{props.title}</h3>

                {props.description && (
                  <div css={Styles.description(props.isFeatured)}>
                    <RichTextHtml html={props.description} />
                  </div>
                )}
              </div>
              {!!props.speakers?.length && (
                <div css={Styles.speakers(props.isFeatured)}>
                  {props.speakers?.map((speaker, index) => (
                    <div
                      key={`${speaker.name}-${speaker.title}-${index}`}
                      css={Styles.speaker}
                    >
                      <h4 css={Styles.speakerName}>{speaker.name}</h4>
                      <p css={Styles.speakerTitle}>{speaker.title}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {props.isFeatured && (
          <div css={Styles.media}>
            {!props.speakers?.length ? (
              <div
                css={Styles.placeholderContainer(props.placeholderImages)}
              ></div>
            ) : (
              <div
                css={Styles.speakerImagesContainer(
                  props.backgroundImages,
                  speakerImages?.length,
                )}
              >
                <div css={Styles.speakerImages(speakerImages.length)}>
                  {speakerImages.map((speaker, index) => (
                    <div
                      css={Styles.speakerImageContainer(
                        speakerImages.length === 1,
                      )}
                      key={`${speaker.imageURL}-${speaker.name}-${index}`}
                    >
                      {speaker.imageURL && (
                        <img
                          alt={speaker.name}
                          css={Styles.speakerImage}
                          src={speaker.imageURL}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </InViewTracking>
  );
}
